import React, { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { Twitter, MessageCircle, Copy, Download, TrendingUp, ArrowUp, ArrowDown } from "lucide-react";

/**
 * Modal showing a stylized trade result card that can be downloaded as PNG
 * or shared via Twitter/Telegram/Copy Link.
 */
export default function TradeShareModal({ trade, asset, open, onOpenChange }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const { settings, loading: settingsLoading } = useSettings();
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  if (!trade) return null;
  const digits = asset?.digits ?? 2;
  const pnl = Number(trade.pnl || 0);
  const roi = trade.amount ? (pnl / trade.amount) * 100 : 0;
  const won = pnl > 0;
  const dirIsUp = trade.direction === "call" || trade.direction === "buy";

  const shareUrl = `${window.location.origin}/share/trade/${trade.id}${user?.referral_code ? `?ref=${user.referral_code}` : ""}`;
  const shareText = `${won ? "Won" : "Lost"} $${Math.abs(pnl).toFixed(2)} (${roi >= 0 ? "+" : ""}${roi.toFixed(1)}%) on ${asset?.name || trade.symbol} · Trade with me on IQ Trade →`;

  const download = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#0A0A0B" });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `iqtrade-${trade.id}.png`;
      a.click();
      toast.success("Downloaded");
    } catch (e) { toast.error("Failed to render image"); }
    finally { setBusy(false); }
  };
  const copyLink = () => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); };
  const twitter = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, "_blank");
  const telegram = () => window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`, "_blank");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="share-modal" className="bg-panel border-line text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white">Share your trade</DialogTitle>
        </DialogHeader>

        {/* Card to be rendered */}
        <div ref={cardRef} data-testid="share-card" className="relative overflow-hidden bg-app border border-line rounded-md p-6" style={{ aspectRatio: "1200/675", width: "100%" }}>
          <div className="absolute inset-0" style={{
            backgroundImage: won
              ? "radial-gradient(600px 300px at 80% 20%, rgba(0,230,118,0.20), transparent 60%)"
              : "radial-gradient(600px 300px at 80% 20%, rgba(255,59,48,0.18), transparent 60%)",
          }} />
          <div className="relative flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {settingsLoading ? (
                <div className="w-8 h-8 bg-transparent"></div>
              ) : settings?.logo_base64 ? (
                <img src={settings.logo_base64} alt="Logo" className="max-h-8" />
              ) : (
                <>
                  <div className="w-8 h-8 bg-buy flex items-center justify-center rounded-sm"><TrendingUp className="w-5 h-5 text-black" /></div>
                  <span className="text-lg font-bold tracking-tight">{settings?.site_title || <>IQ<span className="text-buy">Trade</span></>}</span>
                </>
              )}
            </div>
            <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border ${won ? "border-buy text-buy" : "border-sell text-sell"}`}>
              {won ? "WIN" : "LOSS"}
            </span>
          </div>
          <div className="relative">
            <div className="text-xs text-dim uppercase tracking-widest">{trade.type} · {asset?.name || trade.symbol}</div>
            <div className="flex items-baseline gap-3 mt-2">
              <div className={`text-5xl font-extrabold font-mono ${won ? "text-buy" : "text-sell"}`}>
                {pnl >= 0 ? "+" : ""}${Math.abs(pnl).toFixed(2)}
              </div>
              <div className={`text-lg font-mono ${won ? "text-buy" : "text-sell"}`}>
                {roi >= 0 ? "+" : ""}{roi.toFixed(2)}%
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3 text-xs font-mono">
              <Meta k="Direction" v={<span className={`inline-flex items-center gap-1 ${dirIsUp ? "text-buy" : "text-sell"}`}>{dirIsUp ? <ArrowUp className="w-3 h-3"/> : <ArrowDown className="w-3 h-3"/>}{trade.direction.toUpperCase()}</span>} />
              <Meta k="Amount" v={`$${Number(trade.amount).toFixed(2)}${trade.type === "cfd" ? ` · ${trade.leverage}x` : ""}`} />
              <Meta k="Entry" v={Number(trade.entry_price).toFixed(digits)} />
              {trade.close_price != null && <Meta k="Close" v={Number(trade.close_price).toFixed(digits)} />}
            </div>
          </div>
          <div className="absolute bottom-4 left-6 right-6 flex justify-between items-end">
            <div className="text-[10px] text-dim uppercase tracking-widest">iqtrade.app · {user?.name || "trader"}</div>
            {user?.referral_code && <div className="text-[10px] font-mono text-buy">ref: {user.referral_code}</div>}
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-4 gap-2">
          <button data-testid="share-download" disabled={busy} onClick={download} className="flex flex-col items-center gap-1 py-3 border border-line rounded-sm hover:bg-panel-hover disabled:opacity-50">
            <Download className="w-4 h-4" /><span className="text-[10px] uppercase tracking-widest">PNG</span>
          </button>
          <button data-testid="share-twitter" onClick={twitter} className="flex flex-col items-center gap-1 py-3 border border-line rounded-sm hover:bg-panel-hover">
            <Twitter className="w-4 h-4" /><span className="text-[10px] uppercase tracking-widest">X</span>
          </button>
          <button data-testid="share-telegram" onClick={telegram} className="flex flex-col items-center gap-1 py-3 border border-line rounded-sm hover:bg-panel-hover">
            <MessageCircle className="w-4 h-4" /><span className="text-[10px] uppercase tracking-widest">Telegram</span>
          </button>
          <button data-testid="share-copy" onClick={copyLink} className="flex flex-col items-center gap-1 py-3 border border-line rounded-sm hover:bg-panel-hover">
            <Copy className="w-4 h-4" /><span className="text-[10px] uppercase tracking-widest">Copy</span>
          </button>
        </div>
        <div className="text-[10px] text-dim break-all font-mono bg-app border border-line rounded-sm p-2">{shareUrl}</div>
      </DialogContent>
    </Dialog>
  );
}

const Meta = ({ k, v }) => (
  <div>
    <div className="text-[9px] text-dim uppercase tracking-widest">{k}</div>
    <div className="text-sm mt-0.5">{v}</div>
  </div>
);
